import { TypedDocumentNode, gql } from '@apollo/client';
import { ComponentProps, useState } from 'react';
import styled from 'styled-components';

import { Link } from '@sorare/routing';

import { CustomRewardExperience } from '@sorare/core/src/__generated__/globalTypes';
import { ButtonBase } from '@sorare/core/src/atoms/buttons/ButtonBase';
import { Dropdown } from '@sorare/core/src/atoms/dropdowns/Dropdown';
import { Vertical } from '@sorare/core/src/atoms/layout/flex';
import { BodyS, LabelS } from '@sorare/core/src/atoms/typography';
import { ANY_SPORT_REWARDS_FIXTURE_LEAGUE_QUALITY_RARITY } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { RewardType } from '@sorare/core/src/lib/rewards';
import { generateSportPath } from '@sorare/core/src/lib/routing/generateSportPath';

import { LazyBoxOverviewDialog } from '../LazyBoxOverviewDialog';
import { RewardOverview } from '../RewardOverview';
import { isExperienceType } from '../RewardOverview/utils';
import {
  ClickableRewardOverview_deliverableItem,
  ClickableRewardOverview_probabilisticBundleConfig,
  ClickableRewardOverview_so5League,
  ClickableRewardOverview_so5RewardExperience,
} from './__generated__/index.graphql';

const DropdownLabel = styled.div`
  width: 100%;
  height: 100%;
`;
const DropdownContent = styled.div`
  padding: var(--intermediate-unit) var(--double-unit);
`;

const deliverableItemTypePerExperienceType = {
  ExperienceDeliverableItem: CustomRewardExperience.EVENT,
  JerseyDeliverableItem: CustomRewardExperience.JERSEY,
  TicketsDeliverableItem: CustomRewardExperience.TICKET,
  WearableDeliverableItem: CustomRewardExperience.MERCH,
};

export type Props = ComponentProps<typeof RewardOverview> & {
  so5League?: Nullable<ClickableRewardOverview_so5League>;
  probabilisticBundleConfigs?: ClickableRewardOverview_probabilisticBundleConfig[];
  deliverableItems?: ClickableRewardOverview_deliverableItem[];
  experiences?: ClickableRewardOverview_so5RewardExperience[];
};

export const ClickableRewardOverview = ({
  so5League,
  probabilisticBundleConfigs,
  deliverableItems,
  experiences,
  ...rewardOverviewProps
}: Props) => {
  const { sport } = useSportContext();
  const { type, rarity, quality } = rewardOverviewProps.simpleReward;

  const [openBoxOverview, setOpenBoxOverview] = useState(false);

  const rewardOverview = <RewardOverview {...rewardOverviewProps} />;

  const boxConfig = probabilisticBundleConfigs?.find(
    pb => pb.rarity === rarity
  );

  if (type === RewardType.PROBABILISTIC_BUNDLE && boxConfig) {
    return (
      <>
        <ButtonBase onClick={() => setOpenBoxOverview(true)}>
          {rewardOverview}
        </ButtonBase>
        <LazyBoxOverviewDialog
          open={openBoxOverview}
          onClose={() => setOpenBoxOverview(false)}
          boxConfigId={boxConfig.id}
          so5League={so5League || undefined}
        />
      </>
    );
  }

  const deliverableItem = deliverableItems
    ?.filter(Boolean)
    .find(
      item => type === deliverableItemTypePerExperienceType[item.__typename]
    );

  if (isExperienceType(type) && deliverableItem) {
    return (
      <Dropdown
        triggerOnHover
        gap={8}
        label={props => (
          <ButtonBase {...props}>
            <DropdownLabel>{rewardOverview}</DropdownLabel>
          </ButtonBase>
        )}
      >
        <DropdownContent>
          <Vertical gap={0}>
            <BodyS as="p">{deliverableItem.name}</BodyS>
          </Vertical>
        </DropdownContent>
      </Dropdown>
    );
  }

  const experience = experiences?.find(
    ({ title, type: expType }) => title && expType === type
  );

  if (isExperienceType(type) && experience) {
    return (
      <Dropdown
        triggerOnHover
        gap={8}
        label={props => (
          <ButtonBase {...props}>
            <DropdownLabel>{rewardOverview}</DropdownLabel>
          </ButtonBase>
        )}
      >
        <DropdownContent>
          <Vertical gap={0}>
            <BodyS as="p" bold>
              {experience.title}
            </BodyS>
            {experience.description && (
              <LabelS as="p" color="var(--c-nd-600)">
                {experience.description}
              </LabelS>
            )}
          </Vertical>
        </DropdownContent>
      </Dropdown>
    );
  }

  if (type === RewardType.CARD && quality && so5League && rarity) {
    return (
      <Link
        to={generateSportPath(ANY_SPORT_REWARDS_FIXTURE_LEAGUE_QUALITY_RARITY, {
          params: {
            rarity,
            quality,
            league: so5League.slug,
            fixture: so5League.so5Fixture.slug,
          },
          sport,
        })}
        target="_blank"
        rel="noopener noreferrer"
      >
        {rewardOverview}
      </Link>
    );
  }

  return rewardOverview;
};

ClickableRewardOverview.fragments = {
  probabilisticBundleConfig: gql`
    fragment ClickableRewardOverview_probabilisticBundleConfig on ProbabilisticBundleConfig {
      id
      rarity
    }
  ` as TypedDocumentNode<ClickableRewardOverview_probabilisticBundleConfig>,
  deliverableItem: gql`
    fragment ClickableRewardOverview_deliverableItem on DeliverableItemInterface {
      slug
      id
      name
    }
  ` as TypedDocumentNode<ClickableRewardOverview_deliverableItem>,
  so5RewardExperience: gql`
    fragment ClickableRewardOverview_so5RewardExperience on So5RewardExperience {
      type
      title
      description
    }
  ` as TypedDocumentNode<ClickableRewardOverview_so5RewardExperience>,
  so5League: gql`
    fragment ClickableRewardOverview_so5League on So5League {
      slug
      ...LazyBoxOverviewDialog_so5League
    }
    ${LazyBoxOverviewDialog.fragments.so5League}
  ` as TypedDocumentNode<ClickableRewardOverview_so5League>,
};
